import {
  Button,
  ButtonList,
  Column,
  DropdownButton,
  DropdownButtonContext,
  DropdownButtonContextProps,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { ChangeEvent, ReactElement, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { AppLink } from 'components/appLink';
// eslint-disable-next-line no-restricted-imports
import { actions as companyModalActions } from 'components/shared/companyModal/actions/companyModal';
import { userModalActions } from 'components/usersModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import styles from './contactsSidebarTop.module.scss';

const categories = [
  {
    type: 'user',
    dropdownLabel: 'Contacts',
  },
  {
    type: 'company',
    dropdownLabel: 'Companies',
  },
] as const;

type ContactsCategoryType = (typeof categories)[number]['type'];

export function ContactsSidebarTop(): ReactElement {
  const { pathname } = useLocation();
  const currentCategory = categories.find(({ type }) =>
    pathname.includes(type),
  );
  const dispatch = useAppDispatch();
  const {
    params: { q: searchTerm = '' },
    setParam,
  } = useAppSearchParams('/contacts/user/:id');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState(searchTerm);
  useDebounce(() => setParam('q', searchValue), 300, [searchValue]);

  const getEntityType = () => {
    if (currentCategory?.type === 'user') {
      return 'contact';
    }
    if (currentCategory?.type === 'company') {
      return 'company';
    }

    return '';
  };

  const dropdownButtons = (dropdownProps: DropdownButtonContextProps) => {
    const handleItemClick = (
      path: 'company' | 'user',
      type: ContactsCategoryType,
    ) => {
      if (pathname.includes(`/${path}`)) {
        dropdownProps.closeDropdown();
        return;
      }

      if (type === 'user') {
        ampli.contactsViewEndUsers();
      } else if (type === 'company') {
        ampli.contactsViewCompanies();
      }
      dropdownProps.closeDropdown();
      setSearchValue('');
    };

    return categories.map(({ type, dropdownLabel }) => (
      <AppLink key={type} path={`/contacts/${type}`}>
        <ButtonList.Item
          isSelected={currentCategory?.type === type}
          onClick={() => handleItemClick(type, type)}
        >
          {dropdownLabel}
        </ButtonList.Item>
      </AppLink>
    ));
  };

  const handleSearchInputFocus = () => {
    ampli.contactsSearchFocused();
  };

  const handleSearchInputBlur = () => {
    ampli.contactsSearchBlurred();
  };

  const handleAddClick = () => {
    if (currentCategory?.type === 'user') {
      ampli.contactsAddNewContactClicked();

      dispatch(
        userModalActions.show({
          user: { type: 'contact' },
        }),
      );
    } else if (currentCategory?.type === 'company') {
      ampli.contactsAddNewCompanyClicked();

      dispatch(companyModalActions.companyModalShow());
    }
  };

  const handleSearchSubmit = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setParam('q', '');
    searchInputRef.current?.focus();
  };

  return (
    <>
      <Column.Row slottable borderBottom>
        <Column.Slot>
          <DropdownButton
            noStyle
            buttonProps={{ variant: 'tertiary' }}
            dropdownProps={{ closeOnBlur: true }}
            dropdown={
              <DropdownButtonContext.Consumer>
                {(dropdownProps) => (
                  <ButtonList>{dropdownButtons(dropdownProps)}</ButtonList>
                )}
              </DropdownButtonContext.Consumer>
            }
          >
            <FlexBox alignItems="center">
              <Text.Heading as="h1">
                {currentCategory?.dropdownLabel}
              </Text.Heading>
              <Icon className={styles.dropdownIcon} name="angle-down" />
            </FlexBox>
          </DropdownButton>
        </Column.Slot>

        <Column.Slot noVerticalPadding placement="end">
          <Button
            slotStart={<Icon name="plus" />}
            variant="secondary"
            onClick={handleAddClick}
            data-testid="contacts-add-button"
          >
            Add {getEntityType()}
          </Button>
        </Column.Slot>
      </Column.Row>
      <Column.Row>
        <InputText
          ref={searchInputRef}
          value={searchValue}
          onChange={handleSearchSubmit}
          placeholder="Search..."
          onFocus={handleSearchInputFocus}
          onBlur={handleSearchInputBlur}
          disabled={!currentCategory}
          suffix={{
            type: 'custom',
            value: (
              <FlexBox alignItems="center" padding="0 100">
                <IconButton
                  variant="tertiary"
                  disabled={!searchValue}
                  icon={searchValue ? 'close' : 'search'}
                  onClick={searchValue ? handleClearSearch : undefined}
                />
              </FlexBox>
            ),
            noWrap: true,
          }}
        />
      </Column.Row>
    </>
  );
}
